import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { CompanySubset } from 'app/api/company.api.service';
import { FileUploadEntry } from 'app/api/file-upload.api.service';
import { FileUploadQuery, FileUploadRequested } from 'app/main/file-upload/file-upload.actions';
import { selectFileUploadByCompany, selectFileUploadLoading } from 'app/main/file-upload/file-upload.selectors';
import { AppState } from 'app/reducers';
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-dashboard-card',
    templateUrl: './dashboard-card.component.html',
    styleUrls: ['./dashboard-card.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardCardComponent implements OnInit, OnDestroy {

    @Input()
    set company(value: CompanySubset) {
        this._company.next(value);
    }
    get company(): CompanySubset {
        return this._company.getValue();
    }

    @Output() import = new EventEmitter<CompanySubset>();
    @Output() delete = new EventEmitter<CompanySubset>();

    loading$: Observable<boolean>;
    fileUploads$: BehaviorSubject<FileUploadEntry[]>;
    lastFileUpload$: BehaviorSubject<FileUploadEntry>;
    lastAccountingFileUploadSuccess$: BehaviorSubject<FileUploadEntry>;
    lastBalanceFileUploadSuccess$: BehaviorSubject<FileUploadEntry>;
    lastParametersFileUploadSuccess$: BehaviorSubject<FileUploadEntry>;
    lastPayrollFileUploadSuccess$: BehaviorSubject<FileUploadEntry>;

    // Private
    private _unsubscribeAll: Subject<any>;
    private _company: BehaviorSubject<CompanySubset>;

    constructor(protected store: Store<AppState>) {
        // Set the private defaults
        this.fileUploads$ = new BehaviorSubject([]);
        this.lastFileUpload$ = new BehaviorSubject(undefined);
        this.lastAccountingFileUploadSuccess$ = new BehaviorSubject(undefined);
        this.lastBalanceFileUploadSuccess$ = new BehaviorSubject(undefined);
        this.lastParametersFileUploadSuccess$ = new BehaviorSubject(undefined);
        this.lastPayrollFileUploadSuccess$ = new BehaviorSubject(undefined);
        this._unsubscribeAll = new Subject();
        this._company = new BehaviorSubject(undefined);
    }

    ngOnInit(): void {
        this._company
            .pipe(
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(company => {
                this.refreshFileUploads(company);
            });

        this.loading$ = this.store.pipe(
            select(selectFileUploadLoading(this.company.id))
        );

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectFileUploadByCompany(this.company.id))
            )
            .subscribe(entries => {
                this.fileUploads$.next(entries);
            });

        this.fileUploads$
            .subscribe(entries => {
                for (const entry of entries) {
                    if (entry.key === this.company.lastFileUpload) {
                        this.lastFileUpload$.next(entry);
                        break;
                    }
                }
            });

        }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    refreshFileUploads(company: CompanySubset): void {
        this.store.dispatch(
            new FileUploadRequested({
                companyId: company.id
            } as FileUploadQuery)
        );
    }

    importFile(company: CompanySubset): void {
        this.import.emit(company);
    }

    getFileType(entry: FileUploadEntry): string {
        if (!entry) { return ''; }
        switch (entry.fileType) {
            case 'accounting':
                return 'Comptabilité';
            case 'balance':
                return 'Balance';
            case 'payroll':
                return 'Paye';
            case 'parameters':
                return 'Paramètres';
            case 'template':
                return 'Modèle Export';
            default:
                return 'Inconnu';
        }
    }

    getUntilMonth(untilDateExcluded: string): string {
        return untilDateExcluded && untilDateExcluded !== ''
            ? moment.utc(untilDateExcluded).add(-1, 'month').format('MMMM YYYY')
            : '';
    }

    getDetail(entry: FileUploadEntry): string {
        if (!entry) { return ''; }
        switch (entry.fileType) {
            case 'accounting':
                return entry.accountingSheet.status === 'success'
                    ? (_.isEmpty(entry.accountingSheet.ignoredRows))
                        ? `${entry.accountingSheet.rows} lignes importées`
                        // tslint:disable-next-line: max-line-length
                        : `${entry.accountingSheet.rows} lignes importées, ${entry.accountingSheet.ignoredRows} ignorée(s)`
                    : entry.accountingSheet.message;
            case 'balance':
                return entry.balanceSheet.status === 'success'
                    ? `${entry.balanceSheet.rows} lignes`
                    : entry.balanceSheet.message;
            case 'payroll':
                return entry.payrollSheet.status === 'success'
                    ? (_.isEmpty(entry.payrollSheet.ignoredRows))
                    ? `${entry.payrollSheet.rows} lignes importées`
                    // tslint:disable-next-line: max-line-length
                    : `${entry.payrollSheet.rows} lignes importées, ${entry.payrollSheet.ignoredRows} ignorée(s)`
                    : entry.payrollSheet.message;
            case 'template':
                return '';
            case 'parameters':
                if (entry.importResult.status !== 'success') {
                    return entry.importResult.message;
                }
                const summary: string[] = [];
                if (entry.journalSheet && entry.journalSheet.status === 'success') {
                    summary.push(`Journal (${entry.journalSheet.rows} lignes)`);
                }
                if (entry.planSheet && entry.planSheet.status === 'success') {
                    summary.push(`Plan (${entry.planSheet.rows} lignes)`);
                }
                if (entry.analyticsSheet && entry.analyticsSheet.status === 'success') {
                    summary.push(`Analytics (${entry.analyticsSheet.rows} lignes)`);
                }
                if (entry.customerSheet && entry.customerSheet.status === 'success') {
                    summary.push(`Clients (${entry.customerSheet.rows} lignes)`);
                }
                if (entry.providerSheet && entry.providerSheet.status === 'success') {
                    summary.push(`Fournisseurs (${entry.providerSheet.rows} lignes)`);
                }
                return summary.join(', ');
            default:
                return 'Inconnu';
        }
    }

    deleteOption(company: CompanySubset): void {
        this.delete.emit(company);
    }

}
